import React from 'react';
import { Check, Star } from 'lucide-react';
import { PACKAGE_ACCENTS, ACCESS_CONTROLS, isControlIncluded } from '../../data/accessControls';

const accentOf = (key) => PACKAGE_ACCENTS[key] || PACKAGE_ACCENTS.teal;
const assignedControls = (controls) => ACCESS_CONTROLS.filter((c) => isControlIncluded(controls, c.id));

// Shared subscription package card. Renders a package EXACTLY per its admin-set
// `style` (bar | header | dark | outline) + `accent`, so the same design shows on
// the admin manager and the doctor's plan picker — they can't drift. Callers
// supply only the price area (`renderPrice(onAccent)`) and the action area
// (`footer`); everything else (layout, popular badge, feature list) is identical.
const PackageCardView = ({
  pkg,
  renderPrice,
  footer,
  highlight = false,
  emptyText = 'Core features included',
  cardTestId,
  nameTestId,
  popularTestId,
  namePadStart = false,
}) => {
  const a = accentOf(pkg.accent);
  const isDark = pkg.style === 'dark';
  const isHeader = pkg.style === 'header';
  const isOutline = pkg.style === 'outline';
  const items = assignedControls(pkg.controls);
  // On the admin page each card carries a drag handle pinned to its top-left; add
  // top padding so the handle sits in a gutter above the title instead of over it.
  const handlePad = namePadStart ? 'pt-9' : '';

  const inner = (
    <div
      data-testid={cardTestId}
      className={`relative rounded-2xl overflow-hidden flex flex-col h-full shadow-sm hover:shadow-lg transition-shadow ${
        isDark ? 'bg-[#120d1d] border border-white/10' : 'bg-white'
      } ${isOutline ? '' : isDark ? '' : 'border border-gray-100'} ${
        highlight ? 'ring-1 ring-[#4ABDBD]' : pkg.popular && !isOutline ? 'ring-1 ring-[#4ABDBD]/30' : ''
      }`}
    >
      {pkg.popular && (
        <span
          data-testid={popularTestId}
          className={`absolute top-3 right-3 z-10 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full shadow bg-gradient-to-r ${a.badge}`}
        >
          <Star className="w-3 h-3 fill-current" />
          {pkg.badgeText || 'Most Popular'}
        </span>
      )}

      {pkg.style === 'bar' && <div className={`h-1.5 w-full bg-gradient-to-r ${a.bar}`} />}

      {isHeader && (
        <div className={`px-5 py-4 bg-gradient-to-r ${a.bar} ${handlePad}`}>
          <h3 className="text-lg font-black text-white pr-20" data-testid={nameTestId}>{pkg.name}</h3>
          {renderPrice && renderPrice(true)}
        </div>
      )}

      <div className={`p-5 flex flex-col flex-grow ${isHeader ? '' : handlePad}`}>
        {!isHeader && (
          <>
            <h3 className={`text-lg font-black pr-20 ${isDark ? 'text-white' : 'text-[#1a365d]'}`} data-testid={nameTestId}>{pkg.name}</h3>
            {renderPrice && renderPrice(false)}
          </>
        )}

        <ul className={`space-y-1.5 flex-grow pt-4 mt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-50'}`}>
          {items.length === 0 ? (
            <li className={`text-xs italic ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{emptyText}</li>
          ) : (
            items.map((c) => (
              <li key={c.id} className={`flex items-start gap-2 text-sm ${isDark ? 'text-purple-100/80' : 'text-gray-600'}`}>
                <Check className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? 'text-[#4ABDBD]' : a.text}`} />
                <span>
                  {c.label}
                  {c.type === 'value' && <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>: {pkg.controls[c.id]}</span>}
                </span>
              </li>
            ))
          )}
        </ul>

        {footer && (
          <div className={`mt-5 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-50'}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // 'outline' wraps the card in a gradient border.
  return isOutline ? (
    <div className={`rounded-2xl p-[2px] bg-gradient-to-br ${a.bar} shadow-sm`}>{inner}</div>
  ) : inner;
};

export default PackageCardView;
